/* eslint-disable i18next/no-literal-string */
import classNames from 'classnames'
import type { ReactNode } from 'react'

export type BiBadgeVariant =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
export type BiBadgeSize = 'xxs' | 'xs' | 'sm' | 'md'

// Soft look — light tint + dark text.
const VARIANTS: Record<BiBadgeVariant, string> = {
  neutral: 'bg-bi-surface-dim text-bi-on-surface-var border-bi-outline-medium',
  info: 'bg-bi-light-teal text-bi-charcoal border-bi-deep-teal/30',
  success: 'bg-bi-light-mint text-bi-charcoal border-bi-deep-green/30',
  warning: 'bg-bi-warning-soft text-bi-charcoal border-bi-deep-gold/30',
  error: 'bg-bi-error/10 text-bi-charcoal border-bi-error/30',
}

// Filled look — solid fill + white text (warning keeps charcoal on accent yellow).
const FILLED_VARIANTS: Record<BiBadgeVariant, string> = {
  neutral: 'bg-bi-charcoal text-white border-bi-charcoal',
  info: 'bg-bi-deep-teal text-white border-bi-deep-teal',
  success: 'bg-bi-deep-green text-white border-bi-deep-green',
  warning: 'bg-bi-accent text-bi-charcoal border-bi-deep-gold/40',
  error: 'bg-bi-error text-white border-bi-error',
}

const SIZES: Record<BiBadgeSize, string> = {
  xxs: 'px-1 py-0 text-[8px] leading-3',
  xs: 'px-1.5 py-0 text-[9px] leading-4',
  sm: 'px-1.5 py-0.5 text-[11px]',
  md: 'px-2 py-0.5 text-[11px]',
}

interface BiBadgeProps {
  text: ReactNode
  variant?: BiBadgeVariant
  size?: BiBadgeSize
  pill?: boolean
  filled?: boolean
  removable?: boolean
  onRemove?: () => void
  icon?: ReactNode
  className?: string
}

/** Ported from breakin's Ui::BadgeComponent. */
export function BiBadge({
  text,
  variant = 'neutral',
  size = 'md',
  pill = false,
  filled = false,
  removable = false,
  onRemove,
  icon,
  className,
}: BiBadgeProps) {
  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1 border font-medium',
        (filled ? FILLED_VARIANTS : VARIANTS)[variant],
        SIZES[size],
        pill ? 'rounded-full' : 'rounded',
        className
      )}
    >
      {icon}
      {text}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="-mr-0.5 ml-0.5 opacity-70 transition-opacity hover:opacity-100"
          aria-label="Remove"
        >
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </span>
  )
}
